/// <reference path="./kuunda-js-augment.d.ts" />
import {
  createClient,
  PostgrestBuilder,
  type KuundaClient,
  type KuundaClientOptions,
  type KuundaRealtimeChannel,
  type KuundaResult,
  type Session,
  type User,
} from '@kuunda/kuunda-js';
import {
  assertDbSchema,
  kuundaGoogleOAuthProvider,
  projectRefForOAuth,
  resolveProjectRef,
} from '@/lib/kuundaConfig';

type Countable<T> = KuundaResult<T> & { count?: number | null };

function readPublicEnv(name: string): string | undefined {
  const env = import.meta.env as Record<string, string | undefined>;
  const vite = env[`VITE_${name}`]?.trim();
  const next = env[`NEXT_PUBLIC_${name}`]?.trim();
  return vite || next || undefined;
}

function trimSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function parseContentRange(header: string | null): number | null {
  if (!header) return null;
  const match = header.match(/\/(\d+|\*)$/);
  if (!match || match[1] === '*' || match[1] === undefined) return null;
  return Number(match[1]);
}

function userFromAccessToken(accessToken: string): User {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return { id: '' };
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')),
    ) as Record<string, unknown>;
    const email = typeof json.email === 'string' ? json.email : undefined;
    return {
      id: String(json.sub ?? json.user_id ?? ''),
      email,
      role: typeof json.role === 'string' ? json.role : undefined,
      user_metadata: (json.user_metadata as Record<string, unknown> | undefined) ?? {},
      app_metadata: (json.app_metadata as Record<string, unknown> | undefined) ?? {},
    };
  } catch {
    return { id: '' };
  }
}

export const kuundaUrl = readPublicEnv('KUUNDA_URL');
export const kuundaAnonKey = readPublicEnv('KUUNDA_ANON_KEY');
export const kuundaProjectRefInput = readPublicEnv('KUUNDA_PROJECT_REF');

if (!kuundaUrl || !kuundaAnonKey) {
  console.warn(
    '[ISBD] VITE_KUUNDA_URL / VITE_KUUNDA_ANON_KEY manquants. Configurez apps/web/.env',
  );
}

const rawSchema = readPublicEnv('KUUNDA_DB_SCHEMA');
if (!rawSchema && import.meta.env.DEV) {
  console.warn('[ISBD] VITE_KUUNDA_DB_SCHEMA manquant (proj_ + UUID 32 hex).');
}

export const kuundaDbSchema = rawSchema
  ? assertDbSchema(rawSchema)
  : 'proj_00000000000000000000000000000000';
const dbSchema = kuundaDbSchema;
const projectRef = kuundaUrl
  ? resolveProjectRef(kuundaUrl, kuundaProjectRefInput)
  : undefined;

const globalHeaders: Record<string, string> = {
  'Accept-Profile': dbSchema,
  'Content-Profile': dbSchema,
};
if (projectRef) {
  globalHeaders['X-Project-Ref'] = projectRef;
}

const clientOptions: KuundaClientOptions & { dbSchema: string; projectRef?: string } = {
  dbSchema,
  ...(projectRef ? { projectRef } : {}),
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  },
  global: { headers: globalHeaders },
};

const inner = createClient(
  kuundaUrl || 'http://localhost:54321',
  kuundaAnonKey || 'public-anon-key',
  clientOptions,
);

const proto = PostgrestBuilder.prototype as any;

if (typeof proto.upsert !== 'function') {
  proto.upsert = function upsert(
    this: { insert: (v: unknown) => unknown; filters: string[]; buildPreferParts: () => string[] },
    values: Record<string, unknown> | Record<string, unknown>[],
    options?: { onConflict?: string },
  ) {
    this.insert(values);
    if (options?.onConflict) {
      this.filters.push(`on_conflict=${options.onConflict}`);
    }
    const previous = this.buildPreferParts.bind(this);
    this.buildPreferParts = () => {
      const parts = previous();
      if (!parts.includes('resolution=merge-duplicates')) {
        parts.push('resolution=merge-duplicates');
      }
      return parts;
    };
    return this;
  };
}

if (typeof proto.not !== 'function') {
  proto.not = function not(
    this: { filters: string[] },
    column: string,
    op: string,
    value: unknown,
  ) {
    if (op === 'is' && value === null) {
      this.filters.push(`${column}=not.is.null`);
    } else {
      this.filters.push(`${column}=not.${op}.${formatNotValue(value)}`);
    }
    return this;
  };
}

function formatNotValue(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return encodeURIComponent(String(value));
}

function attachCount<T extends object>(builder: T): T {
  const target = builder as T & {
    fetch: typeof fetch;
    then: (...args: unknown[]) => Promise<unknown>;
    single: () => Promise<unknown>;
    maybeSingle: () => Promise<unknown>;
  };
  let lastCount: number | null = null;
  const origFetch = target.fetch.bind(target);
  target.fetch = (async (input, init) => {
    const res = await origFetch(input, init);
    lastCount = parseContentRange(res.headers.get('content-range'));
    return res;
  }) as typeof fetch;

  const origThen = target.then.bind(target);
  target.then = ((onfulfilled?: (value: unknown) => unknown, onrejected?: (reason: unknown) => unknown) =>
    origThen((value: unknown) => {
      const next = value && typeof value === 'object' ? { ...(value as object), count: lastCount } : value;
      return onfulfilled ? onfulfilled(next) : next;
    }, onrejected)) as typeof target.then;

  const origSingle = target.single.bind(target);
  target.single = async () => {
    const value = await origSingle();
    return value && typeof value === 'object' ? { ...(value as object), count: lastCount } : value;
  };
  const origMaybe = target.maybeSingle.bind(target);
  target.maybeSingle = async () => {
    const value = await origMaybe();
    return value && typeof value === 'object' ? { ...(value as object), count: lastCount } : value;
  };
  return builder;
}

const origFrom = inner.from.bind(inner);
inner.from = ((table: string) => attachCount(origFrom(table))) as typeof inner.from;

async function rpc<R = unknown>(
  fn: string,
  params?: Record<string, unknown>,
): Promise<Countable<R>> {
  const session = inner.auth.getSession();
  const headers = new Headers({
    'Content-Type': 'application/json',
    apikey: kuundaAnonKey || '',
    Authorization: `Bearer ${session?.access_token || kuundaAnonKey || ''}`,
    'Accept-Profile': dbSchema,
    'Content-Profile': dbSchema,
  });
  if (projectRef) headers.set('X-Project-Ref', projectRef);
  const res = await fetch(`${inner.restUrl}/rpc/${encodeURIComponent(fn)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params ?? {}),
  });
  const json = (await res.json().catch(() => null)) as R | { message?: string; msg?: string };
  if (!res.ok) {
    const message =
      json && typeof json === 'object' && 'message' in json
        ? String((json as { message?: string }).message ?? (json as { msg?: string }).msg ?? `HTTP ${res.status}`)
        : `HTTP ${res.status}`;
    return { data: null, error: { message, status: res.status } };
  }
  return { data: json as R, error: null, count: parseContentRange(res.headers.get('content-range')) };
}

inner.rpc = rpc;

inner.removeChannel = (channel: KuundaRealtimeChannel) => {
  channel.unsubscribe();
};

type AuthWithOAuth = typeof inner.auth & {
  signInWithOAuth?: (args: {
    provider: 'google';
    options?: {
      redirectTo?: string;
      skipBrowserRedirect?: boolean;
      queryParams?: Record<string, string>;
    };
  }) => Promise<KuundaResult<{ provider: string; url: string }>>;
  updateUser?: (attrs: {
    password?: string;
    data?: Record<string, unknown>;
  }) => Promise<KuundaResult<User>>;
};

const auth = inner.auth as AuthWithOAuth;

/** Extrait le message d'erreur d'une réponse JSON de la passerelle auth (formats GoTrue legacy et Kuunda). */
function authErrorMessage(json: unknown, res: Response): string {
  if (json !== null && typeof json === 'object') {
    const o = json as Record<string, unknown>;
    for (const key of ['error_description', 'msg', 'message', 'error'] as const) {
      const value = o[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
  }
  return `HTTP ${res.status}`;
}

/**
 * Contournement SDK 0.1.0 : `signInWithPassword` ne lit que `error_description`
 * (format GoTrue legacy) alors que la passerelle Kuunda renvoie
 * `{ code, error_code, msg }`. Sans ce patch, l'UI affiche « HTTP 400 » au lieu
 * du motif réel (identifiants invalides, e-mail non confirmé…).
 */
if (typeof auth.signInWithPassword === 'function') {
  const origSignInWithPassword = auth.signInWithPassword.bind(auth);
  auth.signInWithPassword = async (credentials) => {
    const result = await origSignInWithPassword(credentials);
    if (!result.error || !/^HTTP \d+$/.test(result.error.message) || !kuundaUrl) {
      return result;
    }
    try {
      const res = await fetch(`${trimSlash(kuundaUrl)}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: kuundaAnonKey || '' },
        body: JSON.stringify({ email: credentials.email, password: credentials.password }),
      });
      if (res.ok || res.status !== result.error.status) return result;
      const json = (await res.json().catch(() => null)) as unknown;
      result.error.message = authErrorMessage(json, res);
    } catch {
      /* conserver le message d'origine */
    }
    return result;
  };
}

function mapOAuthAuthorizeError(raw: string): string {
  if (/invalid_api_key/i.test(raw)) {
    return 'Connexion indisponible pour le moment. Réessayez plus tard.';
  }
  if (/unsupported provider|provider is not enabled/i.test(raw)) {
    return 'La connexion Google n’est pas disponible pour le moment. Contactez le support.';
  }
  return sanitizePublicError(raw, 'Connexion Google impossible. Réessayez.');
}

function authApiHeaders(): Headers {
  const headers = new Headers({
    apikey: kuundaAnonKey || '',
    Authorization: `Bearer ${kuundaAnonKey || ''}`,
  });
  if (projectRef) headers.set('X-Project-Ref', projectRef);
  return headers;
}

function resolveGoTrueOAuthProvider(provider: string): { slug: string } | { error: { message: string } } {
  if (provider !== 'google') return { slug: provider };
  const ref = projectRefForOAuth(kuundaUrl || '', projectRef ?? kuundaProjectRefInput);
  if (!ref) {
    return {
      error: {
        message: 'Connexion Google indisponible pour le moment. Réessayez plus tard.',
      },
    };
  }
  return { slug: kuundaGoogleOAuthProvider(ref) };
}

async function probeOAuthAuthorize(href: string): Promise<{ message: string } | null> {
  try {
    const res = await fetch(href, {
      method: 'GET',
      redirect: 'manual',
      headers: authApiHeaders(),
    });
    if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400) || res.ok) {
      return null;
    }
    const json = (await res.json().catch(() => null)) as {
      msg?: string;
      error?: string;
      message?: string;
    } | null;
    const raw = String(json?.msg ?? json?.error ?? json?.message ?? `HTTP ${res.status}`);
    return { message: mapOAuthAuthorizeError(raw) };
  } catch {
    return null;
  }
}

if (typeof auth.signInWithOAuth !== 'function') {
  auth.signInWithOAuth = async ({ provider, options }) => {
    if (!kuundaAnonKey) {
      return { data: null, error: { message: mapOAuthAuthorizeError('invalid_api_key') } };
    }
    const resolved = resolveGoTrueOAuthProvider(provider);
    if ('error' in resolved) return { data: null, error: resolved.error };

    const base =
      projectRefFromAuthorizeBase(kuundaUrl || '', projectRef) || trimSlash(kuundaUrl || '');
    const authorize = new URL(`${base}/auth/v1/authorize`);
    authorize.searchParams.set('provider', resolved.slug);
    // Navigation pleine page : pas de header possible, la gateway Kuunda exige apikey.
    if (kuundaAnonKey) authorize.searchParams.set('apikey', kuundaAnonKey);
    if (options?.redirectTo) authorize.searchParams.set('redirect_to', options.redirectTo);
    if (options?.queryParams) {
      for (const [key, value] of Object.entries(options.queryParams)) {
        authorize.searchParams.set(key, value);
      }
    }
    const href = authorize.toString();
    const authorizeError = await probeOAuthAuthorize(href);
    if (authorizeError) return { data: null, error: authorizeError };

    if (!options?.skipBrowserRedirect && typeof window !== 'undefined') {
      window.location.assign(href);
    }
    return { data: { provider, url: href }, error: null };
  };
}

if (typeof auth.updateUser !== 'function') {
  auth.updateUser = async (attrs) => {
    const session = inner.auth.getSession();
    if (!session?.access_token) {
      return { data: null, error: { message: 'Session expirée' } };
    }
    const headers = new Headers({
      'Content-Type': 'application/json',
      apikey: kuundaAnonKey || '',
      Authorization: `Bearer ${session.access_token}`,
    });
    if (projectRef) headers.set('X-Project-Ref', projectRef);
    const res = await fetch(`${trimSlash(kuundaUrl || '')}/auth/v1/user`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(attrs),
    });
    const json = (await res.json().catch(() => null)) as User | { msg?: string; message?: string };
    if (!res.ok) {
      const message =
        json && typeof json === 'object'
          ? String((json as { msg?: string }).msg ?? (json as { message?: string }).message ?? `HTTP ${res.status}`)
          : `HTTP ${res.status}`;
      return { data: null, error: { message } };
    }
    const user = json as User;
    inner.auth.setSession({ ...session, user });
    return { data: user, error: null };
  };
}

function projectRefFromAuthorizeBase(url: string, ref?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (/^[a-z0-9]{8}\.kuunda-cloud\.com$/i.test(parsed.hostname)) {
      return trimSlash(url);
    }
  } catch {
    return undefined;
  }
  if (ref) return `https://${ref}.kuunda-cloud.com`;
  return undefined;
}

type QueryError = { message: string; status?: number; code?: string } | null;
/** Ligne PostgREST sans schéma généré. */
type QueryRow = any;
type ListResult = { data: QueryRow[] | null; error: QueryError; count?: number | null };
type SingleResult = { data: QueryRow | null; error: QueryError; count?: number | null };
type RequiredSingleResult = { data: QueryRow; error: QueryError; count?: number | null };

interface LooseQuery {
  select: (...args: any[]) => LooseQuery;
  insert: (...args: any[]) => LooseQuery;
  update: (...args: any[]) => LooseQuery;
  upsert: (...args: any[]) => LooseQuery;
  delete: (...args: any[]) => LooseQuery;
  eq: (...args: any[]) => LooseQuery;
  neq: (...args: any[]) => LooseQuery;
  gt: (...args: any[]) => LooseQuery;
  gte: (...args: any[]) => LooseQuery;
  lt: (...args: any[]) => LooseQuery;
  lte: (...args: any[]) => LooseQuery;
  like: (...args: any[]) => LooseQuery;
  ilike: (...args: any[]) => LooseQuery;
  is: (...args: any[]) => LooseQuery;
  in: (...args: any[]) => LooseQuery;
  not: (...args: any[]) => LooseQuery;
  or: (...args: any[]) => LooseQuery;
  order: (...args: any[]) => LooseQuery;
  limit: (...args: any[]) => LooseQuery;
  range: (...args: any[]) => LooseQuery;
  offset: (...args: any[]) => LooseQuery;
  maybeSingle: () => Promise<SingleResult>;
  single: () => Promise<RequiredSingleResult>;
  then: Promise<ListResult>['then'];
}

type AppKuunda = Omit<KuundaClient, 'from' | 'rpc'> & {
  from: (table: string) => LooseQuery;
  rpc: (fn: string, params?: Record<string, unknown>) => Promise<SingleResult & { data: any }>;
  removeChannel: (channel: KuundaRealtimeChannel) => void;
};

export const kuunda = inner as AppKuunda;

export type { Session, User };

/** Hydrate la session depuis `#access_token=` (le SDK Kuunda ne le fait pas tout seul). */
export function sessionFromHash(hash: string): Session | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return null;
  const expires_in = Number(params.get('expires_in') ?? 3600);
  return {
    access_token,
    refresh_token,
    expires_in,
    expires_at: Math.floor(Date.now() / 1000) + expires_in,
    token_type: params.get('token_type') ?? 'bearer',
    user: userFromAccessToken(access_token),
  };
}

export function hydrateSessionFromHash(): Session | null {
  if (typeof window === 'undefined') return null;
  const session = sessionFromHash(window.location.hash ?? '');
  if (session) inner.auth.setSession(session);
  return session ?? inner.auth.getSession();
}

export async function ensureUserProfile(
  user: User,
  extras?: {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    countryCode?: string;
    referredBy?: string | null;
    avatarUrl?: string | null;
  },
): Promise<void> {
  const { data: existing } = await inner.from('profiles').select('id').eq('id', user.id).maybeSingle();
  if (existing) return;

  const meta = user.user_metadata ?? {};
  const { error: rpcError } = await inner.rpc('ensure_user_profile', {
    p_email: user.email ?? null,
    p_first_name:
      (extras?.firstName ?? String(meta.first_name ?? meta.given_name ?? '')) || null,
    p_last_name:
      (extras?.lastName ?? String(meta.last_name ?? meta.family_name ?? '')) || null,
    p_phone_number:
      extras?.phoneNumber ?? (typeof meta.phone_number === 'string' ? meta.phone_number : null),
    p_country_code:
      extras?.countryCode ?? (typeof meta.country_code === 'string' ? meta.country_code : null),
    p_referred_by: extras?.referredBy ?? null,
    p_avatar_url:
      extras?.avatarUrl ??
      (typeof meta.avatar_url === 'string'
        ? meta.avatar_url
        : typeof meta.picture === 'string'
          ? meta.picture
          : null),
    p_referral_token: typeof meta.referral_token === 'string' ? meta.referral_token : null,
  });
  if (!rpcError) return;

  const { error } = await inner.from('profiles').insert({
    id: user.id,
    email: user.email ?? '',
    first_name:
      extras?.firstName ?? String(meta.first_name ?? meta.given_name ?? ''),
    last_name: extras?.lastName ?? String(meta.last_name ?? meta.family_name ?? ''),
    phone_number: extras?.phoneNumber ?? (typeof meta.phone_number === 'string' ? meta.phone_number : null),
    country_code: extras?.countryCode ?? (typeof meta.country_code === 'string' ? meta.country_code : null),
    referred_by: extras?.referredBy ?? null,
    avatar_url:
      extras?.avatarUrl ??
      (typeof meta.avatar_url === 'string'
        ? meta.avatar_url
        : typeof meta.picture === 'string'
          ? meta.picture
          : null),
  });
  if (error && !/duplicate|unique|already exists|23505/i.test(error.message)) {
    console.error(rpcError);
    console.error(error);
  }
}

type FunctionPayload = {
  error?: string;
  message?: string;
  msg?: string;
};

function functionsUrl(name: string): string {
  return `${trimSlash(kuundaUrl ?? '')}/functions/v1/${encodeURIComponent(name)}`;
}

/** Headers Functions : ne pas envoyer Accept-Profile / Content-Profile (403 gateway). */
async function functionAuthHeaders(): Promise<Headers> {
  await inner.auth.initialize();
  // Repli sur la clé anon si aucune session : certaines functions (ex. auth-send-recovery)
  // sont publiques et doivent rester appelables hors connexion (mot de passe oublié).
  const token = inner.auth.getSession()?.access_token ?? kuundaAnonKey ?? '';
  if (!token) throw new Error('Session expirée — reconnectez-vous');
  return new Headers({
    Authorization: `Bearer ${token}`,
    apikey: kuundaAnonKey ?? '',
    'Content-Type': 'application/json',
  });
}

const PUBLIC_SERVICE_ERROR = 'Service indisponible pour le moment. Réessayez dans un instant.';

const INTERNAL_ERROR_RE =
  /\b(XAI_|VITE_|KUUNDA_|BUNNY_|SMTP_|GENIUS_|[A-Z][A-Z0-9]{1,32}_(API_KEY|KEY|URL|SECRET|PASSWORD|TOKEN|HOST|SCHEMA|MODEL|ANON|REF|ZONE))\w*\b|\b(xAI|Grok|OpenAI|ChatGPT|Claude|Gemini|Anthropic|Kuunda|Functions?|handler|invalid_handler|handler_error|esm\.sh|_shared|Deno|request_id|ai-ideation|ai-analyze|bunny-storage|genius-pay|auth-send-recovery|network-notify)\b|index\.ts|platform\/kuunda|export default|Console Kuunda|kuunda-cloud|\.env\b/i;

export function sanitizePublicError(text: string, fallback: string): string {
  const value = text.trim();
  if (!value) return fallback;
  if (INTERNAL_ERROR_RE.test(value)) return fallback;
  return value;
}

export function kuundaErrorMessage(err: unknown, fallback: string): string {
  let raw = '';
  if (err instanceof Error && err.message.trim()) raw = err.message;
  else if (err && typeof err === 'object') {
    const o = err as { message?: unknown; error?: unknown };
    if (typeof o.message === 'string' && o.message.trim()) raw = o.message;
    else if (typeof o.error === 'string' && o.error.trim()) raw = o.error;
  }
  if (!raw) return fallback;
  return sanitizePublicError(raw, fallback);
}

function functionErrorMessage(_name: string, res: Response, payload: FunctionPayload): string {
  const raw = payload.error ?? payload.message ?? payload.msg;
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (res.status === 401 || /non authentifié|session invalide|session expir/i.test(text)) {
    return 'Session expirée — reconnectez-vous';
  }
  return sanitizePublicError(text, PUBLIC_SERVICE_ERROR);
}

async function invokeKuundaFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(functionsUrl(name), {
    method: 'POST',
    headers: await functionAuthHeaders(),
    body: JSON.stringify(body),
  });
  const payload = ((await res.json().catch(() => ({}))) ?? {}) as T & FunctionPayload;
  if (!res.ok || (typeof payload === 'object' && payload && payload.error)) {
    throw new Error(functionErrorMessage(name, res, payload));
  }
  return payload as T;
}

export async function invokeAiFunction<T>(
  name: 'ai-ideation' | 'ai-analyze',
  body: Record<string, unknown>,
): Promise<T> {
  return invokeKuundaFunction<T>(name, body);
}

export async function invokeEdgeFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  return invokeKuundaFunction<T>(name, body);
}
